import {Subscription} from "rxjs";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {AfterViewInit, Component, inject, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";

import {appRoutes} from "@numizmat/constants";
import {environment} from "@numizmat/environments";
import {PasswordRecoveryApiService} from "@numizmat/services/account";
import {LogoComponent, PageHeaderComponent} from "@numizmat/components";
import {MetaTagService, ScrollerService} from "@numizmat/services/common";
import {ConfirmPasswordRecoveryRequest} from "@numizmat/services-models/account";

@Component({
  selector: 'nz-password-recovery-confirm',
  standalone: true,
  imports: [
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    PageHeaderComponent,
    LogoComponent
  ],
  templateUrl: './password-recovery-confirm.component.html',
  styleUrl: './password-recovery-confirm.component.scss'
})
export class PasswordRecoveryConfirmComponent implements OnInit, AfterViewInit, OnDestroy {
  private router: Router = inject(Router);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private metaTagService: MetaTagService = inject(MetaTagService);
  private scrollerService: ScrollerService = inject(ScrollerService);
  private passwordRecoveryApiService: PasswordRecoveryApiService = inject(PasswordRecoveryApiService);

  private passwordRecoveryToken: string | null = null;
  private confirmPasswordRecoverySubscription?: Subscription;

  protected readonly appRoutes = appRoutes;

  protected loading: boolean = false;
  protected errorMessage: string = '';

  protected form: FormGroup = this.initializeForm();

  public ngOnInit(): void {
    this.passwordRecoveryToken = this.activatedRoute.snapshot.queryParamMap.get('token');
    if (!this.passwordRecoveryToken) {
      this.router.navigate([appRoutes.home]).then()
    }
    this.setMetaTags();
  }

  public ngAfterViewInit(): void {
    this.scrollerService.scrollToTop();
  }

  public ngOnDestroy(): void {
    this.confirmPasswordRecoverySubscription?.unsubscribe();
  }

  protected onSubmit(): void {
    this.loading = true;
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.loading = false;
      return;
    }
    const formValue = this.form.getRawValue();
    const request: ConfirmPasswordRecoveryRequest = {
      password: formValue.password,
      passwordRecoveryToken: !!this.passwordRecoveryToken ? this.passwordRecoveryToken : ''
    };
    this.confirmPasswordRecoverySubscription = this.passwordRecoveryApiService.confirmPasswordRecovery(request).subscribe({
      next: response => {
        this.loading = false;
        if (response.success) {
          this.router.navigate([appRoutes.account, appRoutes.passwordRecoverySuccess]).then()
        } else {
          this.errorMessage = response.errorMessage;
        }
      }
    });
  }

  private initializeForm() {
    return new FormGroup({
      password: new FormControl(null, [Validators.required, Validators.minLength(8)]),
      confirmPassword: new FormControl(null, [Validators.required, Validators.minLength(8)]),
    });
  }

  private setMetaTags(): void {
    this.metaTagService.setCanonicalUrl(`${environment.applicationDomain}/${appRoutes.account}/${appRoutes.passwordRecoveryConfirm}`);
    this.metaTagService.setTitle('Възстановяване на парола | Collectoria');
    this.metaTagService.setDescription('Възстановяване на парола | Collectoria');
  }

  get password() {
    return this.form.get('password');
  }

  get confirmPassword() {
    return this.form.get('confirmPassword');
  }
}
